/* The three hooks the system exports. Every one is safe to call from any component. */
import React from 'react';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Focus trap: Tab stays inside `ref`, focus returns to the opener when inactive.
 *
 * @param {{ current: HTMLElement|null }} ref
 * @param {boolean} active
 */
export function useFocusTrap(ref, active) {
  React.useEffect(() => {
    if (!active || !ref.current) return;
    const opener = document.activeElement;
    const box = ref.current;
    const first = () => box.querySelectorAll(FOCUSABLE)[0];
    const t = setTimeout(() => { if (!box.contains(document.activeElement)) { const f = box.querySelector('[data-autofocus]') || first(); f && f.focus(); } }, 20);
    const key = (e) => {
      if (e.key !== 'Tab') return;
      const els = Array.from(box.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!els.length) return;
      const a = els[0], z = els[els.length - 1];
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    box.addEventListener('keydown', key);
    return () => { clearTimeout(t); box.removeEventListener('keydown', key); if (opener && opener.focus && document.contains(opener)) opener.focus(); };
  }, [active]);
}

/**
 * Bind a key: 'mod+k' (⌘/Ctrl+K), '/', 'n'. Plain keys are ignored while typing.
 *
 * @param {string} combo
 * @param {(e: KeyboardEvent) => void} fn
 */
export function useHotkey(combo, fn) {
  React.useEffect(() => { const k = (e) => { const want = combo.toLowerCase().split('+'); const key = want[want.length - 1]; const mod = want.includes('mod'); if (e.key.toLowerCase() === key && (!mod || e.metaKey || e.ctrlKey)) { const t = e.target; if (!mod && t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return; e.preventDefault(); fn(e); } }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [combo, fn]);
}

/**
 * The current html[data-theme], live.
 *
 * @returns {'light'|'dark'}
 */
export function useTheme() {
  const get = () => document.documentElement.getAttribute('data-theme') || 'light';
  const [t, setT] = React.useState(get);
  React.useEffect(() => { const o = new MutationObserver(() => setT(get())); o.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] }); return () => o.disconnect(); }, []);
  return t;
}
